import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const timeline = [
  { year: "2023", title: "h动漫 诞生", desc: "几位热爱二次元的编辑凑在一起，决定做一个「认真聊动漫」的内容站。" },
  { year: "2024", title: "作品资料库上线", desc: "覆盖数百部作品的资料库正式上线，支持多维筛选与检索。" },
  { year: "2025", title: "评论专栏扩展", desc: "成立独立评论栏目，邀请业内作者提供深度解析与行业观察。" },
  { year: "2026", title: "全新改版", desc: "全站视觉与浏览体验升级，聚焦资讯速度与作品展示的双重需求。" },
];

const values = [
  { icon: "ri-flashlight-line", title: "资讯先行", desc: "第一时间跟进新番定档、预告与行业动态，让你不错过任何热点。" },
  { icon: "ri-book-open-line", title: "内容专业", desc: "从世界观到制作幕后，用专业视角提供有深度的作品解析。" },
  { icon: "ri-compass-3-line", title: "用心推荐", desc: "拒绝流水线推荐，只为真正值得一看的作品发声。" },
];

export default function AboutPage() {
  useDocumentMeta(
    "关于我们 - h动漫",
    "了解h动漫的定位、愿景与服务范围，我们致力于为二次元爱好者提供权威的动漫资讯与作品推荐。"
  );

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="max-w-4xl mx-auto">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <header className="mt-4">
          <h1 className="font-heading font-extrabold text-foreground-950 text-3xl md:text-4xl">关于 h动漫</h1>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
            h动漫是面向二次元爱好者的动漫资讯与作品推荐平台。我们相信，动漫不只是娱乐，
            更是一种值得被认真对待的表达方式。因此，我们既追求资讯的速度，也坚持内容的深度。
          </p>
        </header>

        <section className="mt-8 rounded-lg bg-primary-500 text-background-50 p-6 md:p-8">
          <h2 className="font-heading font-bold text-xl md:text-2xl">平台定位</h2>
          <p className="mt-3 text-sm md:text-base leading-loose text-background-100/95">
            我们聚焦三件事：发布最新动漫资讯、建立丰富的作品资料库、提供专业的评论与分析。
            无论你是想快速了解本季新番，还是想深入挖掘某部作品背后的故事，h动漫都希望成为你第一个打开的地方。
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { num: "2000+", label: "收录作品" },
              { num: "500+", label: "深度评论" },
              { num: "50+", label: "合作作者" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-background-50/10 px-4 py-3">
                <p className="font-heading font-extrabold text-2xl">{s.num}</p>
                <p className="mt-0.5 text-xs text-background-100/90">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">我们的坚持</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${v.icon} text-xl`} />
                </div>
                <h3 className="mt-3 font-heading font-semibold text-foreground-950 text-base">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">发展历程</h2>
          <ol className="mt-5 relative border-l border-background-300 pl-6 space-y-6">
            {timeline.map((t) => (
              <li key={t.year} className="relative">
                <span className="absolute -left-[31px] top-1 w-3.5 h-3.5 rounded-full bg-primary-500 ring-4 ring-background-50" />
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-800 text-xs font-semibold">{t.year}</span>
                  <h3 className="font-heading font-semibold text-foreground-950 text-base">{t.title}</h3>
                </div>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{t.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10 rounded-lg border border-background-200/70 bg-background-100 p-6 text-center">
          <h2 className="font-heading font-semibold text-foreground-950 text-lg">想和我们聊聊？</h2>
          <p className="mt-2 text-sm text-foreground-600">无论是内容建议、合作洽谈还是纠错反馈，我们都非常欢迎。</p>
          <div className="mt-4 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              联系我们 <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-md bg-background-50 text-foreground-700 text-sm font-medium border border-background-300 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}